import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Activity, 
  Server, 
  ShieldCheck, 
  AlertTriangle, 
  Clock, 
  Trash2, 
  Plus, 
  RefreshCw,
  Globe,
  Lock,
  Unlock,
  Upload
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Status Badge Component
const StatusBadge = ({ status }) => {
  const getStatusConfig = () => {
    switch (status) {
      case "UP":
        return {
          color: "bg-green-500",
          glow: "shadow-[0_0_10px_rgba(34,197,94,0.5)]",
          text: "UP",
          textColor: "text-green-500"
        };
      case "DOWN":
        return {
          color: "bg-red-500",
          glow: "shadow-[0_0_10px_rgba(239,68,68,0.5)]",
          text: "DOWN",
          textColor: "text-red-500",
          pulse: true
        };
      case "PENDING_VERIFICATION":
        return {
          color: "bg-yellow-500",
          glow: "shadow-[0_0_10px_rgba(234,179,8,0.5)]",
          text: "VERIFYING",
          textColor: "text-yellow-500",
          pulse: true
        };
      default:
        return {
          color: "bg-zinc-500",
          glow: "",
          text: "UNKNOWN",
          textColor: "text-zinc-500"
        };
    }
  };

  const config = getStatusConfig();

  return (
    <div className="flex items-center gap-2" data-testid={`status-badge-${status?.toLowerCase()}`}>
      <div 
        className={`w-3 h-3 rounded-full ${config.color} ${config.glow} ${config.pulse ? 'animate-pulse' : ''}`}
      />
      <span className={`font-mono text-sm font-bold ${config.textColor}`}>
        {config.text}
      </span>
    </div>
  );
};

// SSL Badge Component
const SSLBadge = ({ isHttps, sslExpiry, sslDaysRemaining }) => {
  if (!isHttps) {
    return (
      <div className="flex items-center gap-2 text-zinc-500" data-testid="ssl-badge-http">
        <Unlock className="w-4 h-4" />
        <span className="text-xs font-mono">HTTP</span>
      </div>
    );
  }

  const getSSLStatus = () => {
    if (sslDaysRemaining === null || sslDaysRemaining === undefined) {
      return { color: "text-zinc-500", label: "Checking...", icon: ShieldCheck };
    }
    if (sslDaysRemaining < 0) {
      return { color: "text-red-500", label: "EXPIRED", icon: AlertTriangle };
    }
    if (sslDaysRemaining <= 7) {
      return { color: "text-amber-500", label: `${sslDaysRemaining}d left`, icon: AlertTriangle };
    }
    return { color: "text-green-500", label: `${sslDaysRemaining}d`, icon: ShieldCheck };
  };

  const sslStatus = getSSLStatus();

  return (
    <div className={`flex items-center gap-2 ${sslStatus.color}`} data-testid="ssl-badge-https">
      <Lock className="w-4 h-4" />
      <span className="text-xs font-mono">SSL: {sslStatus.label}</span>
      {sslExpiry && (
        <span className="text-xs text-zinc-600 hidden md:inline">
          ({new Date(sslExpiry).toLocaleDateString()})
        </span>
      )}
    </div>
  );
};

// Website Card Component
const WebsiteCard = ({ monitor, onDelete, onRefresh, isRefreshing }) => {
  const formatLastChecked = (timestamp) => {
    if (!timestamp) return "Never";
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  return (
    <Card 
      className="bg-zinc-900 border border-zinc-800 rounded-sm hover:border-zinc-600 transition-colors"
      data-testid={`website-card-${monitor.id}`}
    >
      <CardContent className="p-6">
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Globe className="w-5 h-5 text-zinc-400 flex-shrink-0" />
            <a 
              href={monitor.url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="font-mono text-sm text-zinc-100 truncate hover:text-white hover:underline"
              data-testid={`website-url-${monitor.id}`}
            >
              {monitor.url}
            </a>
          </div>
          <StatusBadge status={monitor.last_status} />
        </div>

        <div className="space-y-3">
          <SSLBadge 
            isHttps={monitor.is_https} 
            sslExpiry={monitor.ssl_expiry}
            sslDaysRemaining={monitor.ssl_days_remaining}
          />
          
          <div className="flex items-center gap-2 text-zinc-500">
            <Clock className="w-4 h-4" />
            <span className="text-xs font-mono">
              Last: {formatLastChecked(monitor.last_checked)}
            </span>
          </div>

          {/* Playwright verification indicator */}
          {monitor.verified_by_playwright && (
            <div className="flex items-center gap-2 text-blue-400 text-xs font-mono">
              <ShieldCheck className="w-4 h-4" />
              <span>Verified by browser check</span>
            </div>
          )}

          {monitor.error_message && (
            <div className="flex items-start gap-2 text-red-500 bg-red-950/30 p-2 rounded-sm">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span className="text-xs font-mono break-all">
                {monitor.error_message}
              </span>
            </div>
          )}

          {/* Screenshot link for DOWN sites */}
          {monitor.last_status === "DOWN" && monitor.screenshot_path && (
            <a 
              href={`${BACKEND_URL}/api/monitor/${monitor.id}/screenshot`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-xs font-mono text-amber-400 hover:text-amber-300 underline"
              data-testid={`screenshot-link-${monitor.id}`}
            >
              📸 View downtime screenshot
            </a>
          )}
        </div>

        <div className="flex gap-2 mt-4 pt-4 border-t border-zinc-800">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRefresh(monitor.id)}
            disabled={isRefreshing}
            className="flex-1 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-sm font-mono text-xs"
            data-testid={`refresh-btn-${monitor.id}`}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            CHECK
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(monitor.id)}
            className="text-red-500 hover:text-red-400 hover:bg-red-950/30 rounded-sm font-mono text-xs"
            data-testid={`delete-btn-${monitor.id}`}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            DELETE
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Stats Card Component
const StatsCard = ({ icon: Icon, label, value, color = "text-zinc-100" }) => (
  <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4" data-testid={`stats-${label.toLowerCase()}`}>
    <div className="flex items-center gap-3">
      <Icon className={`w-5 h-5 ${color}`} />
      <div>
        <p className="text-xs text-zinc-500 font-mono uppercase tracking-wider">{label}</p>
        <p className={`text-2xl font-mono font-bold ${color}`}>{value}</p>
      </div>
    </div>
  </div>
);

// Main Uptime Monitor Component
export const UptimeMonitor = () => {
  const [monitors, setMonitors] = useState([]);
  const [stats, setStats] = useState({ total: 0, up: 0, down: 0, ssl_warning: 0 });
  const [newUrl, setNewUrl] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [refreshingId, setRefreshingId] = useState(null);
  const [isRefreshingAll, setIsRefreshingAll] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [filter, setFilter] = useState("all");

  // Filter monitors based on selected filter
  const filteredMonitors = monitors.filter((monitor) => {
    switch (filter) {
      case "down":
        return monitor.last_status === "DOWN";
      case "up":
        return monitor.last_status === "UP";
      case "ssl_warning":
        return monitor.ssl_days_remaining !== null && monitor.ssl_days_remaining <= 7;
      case "ssl_expired":
        return monitor.ssl_days_remaining !== null && monitor.ssl_days_remaining < 0;
      default:
        return true;
    }
  });

  const fetchMonitors = useCallback(async () => {
    try {
      const [monitorsRes, statsRes] = await Promise.all([
        axios.get(`${API}/monitor`),
        axios.get(`${API}/stats`)
      ]);
      setMonitors(monitorsRes.data);
      setStats(statsRes.data);
    } catch (e) {
      console.error("Failed to fetch monitors:", e);
      toast.error("Failed to load monitors");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMonitors();
    const interval = setInterval(fetchMonitors, 30000);
    return () => clearInterval(interval);
  }, [fetchMonitors]);

  const handleAddMonitor = async (e) => {
    e.preventDefault();
    if (!newUrl.trim()) return;

    setIsAdding(true);
    try {
      await axios.post(`${API}/monitor`, { url: newUrl });
      toast.success("Website added to monitoring");
      setNewUrl("");
      fetchMonitors();
    } catch (e) {
      const message = e.response?.data?.detail || "Failed to add website";
      toast.error(message);
    } finally {
      setIsAdding(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/monitor/${id}`);
      toast.success("Website removed from monitoring");
      fetchMonitors();
    } catch (e) {
      toast.error("Failed to delete website");
    }
  };

  const handleRefresh = async (id) => {
    setRefreshingId(id);
    try {
      await axios.post(`${API}/monitor/${id}/check`);
      toast.success("Check completed");
      fetchMonitors();
    } catch (e) {
      toast.error("Check failed");
    } finally {
      setRefreshingId(null);
    }
  };

  const handleRefreshAll = async () => {
    setIsRefreshingAll(true);
    try {
      await axios.post(`${API}/monitor/check-all`);
      toast.success("All websites checked");
      fetchMonitors();
    } catch (e) {
      toast.error("Failed to check websites");
    } finally {
      setIsRefreshingAll(false);
    }
  };

  const handleCsvImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/monitor/import-csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const { added, skipped, errors } = response.data;
      
      if (added.length > 0) {
        toast.success(`Imported ${added.length} website(s)`);
      }
      if (skipped.length > 0) {
        toast.info(`Skipped ${skipped.length} duplicate(s)`);
      }
      if (errors.length > 0) {
        toast.error(`${errors.length} URL(s) had errors`);
      }
      
      fetchMonitors();
    } catch (e) {
      const message = e.response?.data?.detail || "Failed to import CSV";
      toast.error(message);
    } finally {
      setIsImporting(false);
      event.target.value = '';
    }
  };

  return (
    <div className="text-zinc-100">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#09090b]/80 border-b border-zinc-800">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Activity className="w-7 h-7 text-green-500" />
              <div>
                <h1 className="font-mono text-lg font-bold tracking-tight">Uptime Monitoring</h1>
                <p className="text-xs text-zinc-500 font-mono">Real-time website monitoring</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex bg-zinc-900 border border-zinc-700 rounded-sm overflow-hidden">
                <button
                  onClick={() => setFilter("all")}
                  className={`px-3 py-1.5 font-mono text-xs transition-colors ${filter === "all" ? "bg-zinc-700 text-white" : "text-zinc-400 hover:text-white hover:bg-zinc-800"}`}
                  data-testid="filter-all"
                >
                  All ({monitors.length})
                </button>
                <button
                  onClick={() => setFilter("up")}
                  className={`px-3 py-1.5 font-mono text-xs border-l border-zinc-700 transition-colors ${filter === "up" ? "bg-green-900/50 text-green-400" : "text-zinc-400 hover:text-green-400 hover:bg-zinc-800"}`}
                  data-testid="filter-up"
                >
                  Up ({stats.up})
                </button>
                <button
                  onClick={() => setFilter("down")}
                  className={`px-3 py-1.5 font-mono text-xs border-l border-zinc-700 transition-colors ${filter === "down" ? "bg-red-900/50 text-red-400" : "text-zinc-400 hover:text-red-400 hover:bg-zinc-800"}`}
                  data-testid="filter-down"
                >
                  Down ({stats.down})
                </button>
                <button
                  onClick={() => setFilter("ssl_warning")}
                  className={`px-3 py-1.5 font-mono text-xs border-l border-zinc-700 transition-colors ${filter === "ssl_warning" ? "bg-amber-900/50 text-amber-400" : "text-zinc-400 hover:text-amber-400 hover:bg-zinc-800"}`}
                  data-testid="filter-ssl"
                >
                  SSL ({stats.ssl_warning})
                </button>
              </div>
              <Button
                onClick={handleRefreshAll}
                disabled={isRefreshingAll}
                className="bg-white text-black hover:bg-zinc-200 rounded-sm font-mono text-xs uppercase tracking-wider"
                data-testid="refresh-all-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshingAll ? 'animate-spin' : ''}`} />
                Check All
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="p-6">
        {/* Stats Section */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="stats-section">
          <StatsCard icon={Server} label="Total" value={stats.total} />
          <StatsCard icon={Activity} label="Up" value={stats.up} color="text-green-500" />
          <StatsCard icon={AlertTriangle} label="Down" value={stats.down} color="text-red-500" />
          <StatsCard icon={ShieldCheck} label="SSL Warning" value={stats.ssl_warning} color="text-amber-500" />
        </section>

        {/* Add Website Form */}
        <section className="mb-8" data-testid="add-form-section">
          <form onSubmit={handleAddMonitor} className="flex gap-3">
            <div className="flex-1 relative">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <Input
                type="url"
                placeholder="https://example.com"
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)}
                className="pl-11 bg-zinc-900 border-zinc-800 rounded-sm font-mono text-sm placeholder:text-zinc-600 focus:border-zinc-600 focus:ring-0"
                data-testid="url-input"
                required
              />
            </div>
            <Button
              type="submit"
              disabled={isAdding || !newUrl.trim()}
              className="bg-white text-black hover:bg-zinc-200 rounded-sm font-mono text-sm uppercase tracking-wider px-6"
              data-testid="add-website-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              {isAdding ? "Adding..." : "Add"}
            </Button>
            <div className="relative">
              <input
                type="file"
                accept=".csv"
                onChange={handleCsvImport}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                data-testid="csv-file-input"
                disabled={isImporting}
              />
              <Button
                type="button"
                variant="outline"
                disabled={isImporting}
                className="border-zinc-700 bg-zinc-900 text-zinc-300 hover:bg-zinc-800 hover:text-white rounded-sm font-mono text-sm uppercase tracking-wider"
                data-testid="import-csv-btn"
              >
                <Upload className={`w-4 h-4 mr-2 ${isImporting ? 'animate-pulse' : ''}`} />
                {isImporting ? "Importing..." : "CSV"}
              </Button>
            </div>
          </form>
          <p className="text-xs text-zinc-600 font-mono mt-2">
            CSV format: one URL per line or with "url" column header
          </p>
        </section>

        {/* Monitors Grid */}
        <section data-testid="monitors-section">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 animate-pulse">
                  <div className="h-4 bg-zinc-800 rounded w-3/4 mb-4" />
                  <div className="h-3 bg-zinc-800 rounded w-1/2 mb-2" />
                  <div className="h-3 bg-zinc-800 rounded w-1/3" />
                </div>
              ))}
            </div>
          ) : monitors.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-zinc-800 rounded-sm" data-testid="empty-state">
              <Server className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-500 font-mono">No websites monitored yet</p>
              <p className="text-zinc-600 text-sm font-mono mt-1">Add a URL above to start monitoring</p>
            </div>
          ) : filteredMonitors.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-zinc-800 rounded-sm" data-testid="no-results-state">
              <Activity className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-500 font-mono">No websites match this filter</p>
              <Button
                variant="ghost"
                onClick={() => setFilter("all")}
                className="mt-2 text-zinc-400 hover:text-white font-mono text-sm"
                data-testid="clear-filter-btn"
              >
                Clear filter
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredMonitors.map((monitor) => (
                <WebsiteCard
                  key={monitor.id}
                  monitor={monitor}
                  onDelete={handleDelete}
                  onRefresh={handleRefresh}
                  isRefreshing={refreshingId === monitor.id}
                />
              ))}
            </div>
          )}
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-zinc-800 mt-8">
        <div className="px-6 py-4">
          <p className="text-center text-xs text-zinc-600 font-mono">
            Scheduler runs every 5 minutes • Notifications via Slack & Email • SSL warnings at 7 days
          </p>
        </div>
      </footer>
    </div>
  );
};

export default UptimeMonitor;
